// theme/resolveEffectiveTheme.ts
import type { ThemePreference } from '@covered-calls/shared';

export type { ThemePreference };
export type EffectiveTheme = 'light' | 'dark';
export type OsPreference = 'light' | 'dark' | 'unknown';

/**
 * Pure: no DOM access, no side effects. Single source of truth mapping
 * (preference, osPreference) -> effective theme. (Req 4.1-4.4, 5.8, 7.2)
 */
export function resolveEffectiveTheme(
  preference: ThemePreference,
  osPreference: OsPreference,
): EffectiveTheme {
  switch (preference) {
    case 'Light': return 'light';
    case 'Dark': return 'dark';
    case 'System':
      if (osPreference === 'light') return 'light';
      if (osPreference === 'dark') return 'dark';
      return 'dark'; // unknown -> dark fallback (Req 4.3)
  }
}

/** Normalises an unrecognised/missing stored value to 'System'. (Req 5.6, 7.4) */
export function normaliseStoredPreference(value: unknown): ThemePreference {
  return value === 'Light' || value === 'Dark' || value === 'System'
    ? value
    : 'System';
}

/** Reads the OS preference via matchMedia; returns 'unknown' when unavailable. */
export function readOsPreference(): OsPreference {
  if (typeof window === 'undefined' || !window.matchMedia) return 'unknown';
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'unknown';
}
